import { LoaderBar } from 'components'
import { GetApprovalSignDetail, GetApprovalSignHistory } from 'hooks'
import { useNotification } from 'hooks/toast'
import {
    ApproverLandingView,
    RejectReportModal,
    ScreenContainer,
    extractApiError,
} from 'page-components/report-approval'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Message } from 'semantic-ui-react'
import { ErrorConstant, ReportApprovalService } from 'utility'

// No-login approval page: the e-mailed link <web>/public/approval/<token>. The token is bound to ONE approval step
// (7 days, single use); the server answers 410 for an expired / used / no longer current link.
const LINK_GONE =
    'This approval link has expired, was already used, or is no longer needed. If you have an eAuditor login, open Report Approvals; otherwise ask the admin to resend the approval e-mail.'
const linkMessage = err =>
    err?.status === 410 ? LINK_GONE : extractApiError(err)

export const ReportApprovalSignScreen = () => {
    const { token } = useParams()
    const { successToast, errorToast } = useNotification()
    const { data, loading, error } = GetApprovalSignDetail(token)
    const { data: history, loading: historyLoading } =
        GetApprovalSignHistory(data ? token : null)
    const [rejectOpen, setRejectOpen] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [doneMessage, setDoneMessage] = useState('')

    const handleApprove = async payload => {
        try {
            setSubmitting(true)
            setSubmitError('')
            const result = await ReportApprovalService.advanceSign(
                token,
                payload,
            )
            const fully = !!(result?.IsFullyApproved ?? result?.isFullyApproved)
            setDoneMessage(
                fully
                    ? 'Thank you. The report is now approved.'
                    : 'Thank you. Your approval has been recorded and the report has moved to the next approver.',
            )
            successToast('Approval recorded')
        } catch (err) {
            const message = linkMessage(err)
            setSubmitError(message)
            errorToast(message)
        } finally {
            setSubmitting(false)
        }
    }

    const handleReject = async payload => {
        try {
            setSubmitting(true)
            setSubmitError('')
            await ReportApprovalService.rejectSign(token, payload)
            setRejectOpen(false)
            setDoneMessage(
                'The report was rejected and returned to the auditor.',
            )
            successToast('Report rejected')
        } catch (err) {
            const message = linkMessage(err)
            setSubmitError(message)
            errorToast(message)
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) {
        return <LoaderBar content="Loading report…" />
    }

    if (error || !data) {
        return (
            <ScreenContainer className="p-4">
                <Message negative>
                    {/* a network / server error keeps its own text; anything else is a dead link */}
                    {error === ErrorConstant.default ? error : LINK_GONE}
                </Message>
            </ScreenContainer>
        )
    }

    if (doneMessage) {
        return (
            <ScreenContainer className="p-4">
                <Message positive>{doneMessage}</Message>
            </ScreenContainer>
        )
    }

    return (
        <ScreenContainer className="p-4">
            <ApproverLandingView
                report={data}
                history={history}
                historyLoading={historyLoading}
                currentUserName={data?.waitingOn || ''}
                canReject={data?.canReject !== false}
                linkExpiresOn={data?.linkExpiresOn}
                onApprove={handleApprove}
                onReject={() => setRejectOpen(true)}
                submitting={submitting}
                error={submitError}
            />
            <RejectReportModal
                open={rejectOpen}
                onClose={() => setRejectOpen(false)}
                onSubmit={handleReject}
                submitting={submitting}
                error={submitError}
                auditorName={data?.auditorName || 'the auditor'}
            />
        </ScreenContainer>
    )
}
